import { useState, useEffect, type ChangeEvent, type KeyboardEvent } from "react";
import Modal from "@modalSrc/Modal";

type StringInputModalProps = {
    isOpen: boolean;
    onClose?: (saved: boolean) => void;
    onSubmit?: (value: string) => void;
    title?: string;
    label?: string;
    buttonText?: string;
    placeholder?: string;
    initialValue?: string;
    required?: boolean;
    maxLength?: number | null;
    validation?: ((value: string) => string | null) | null;
    inputType?: "text" | "password";
};

export default function StringInputModal({ 
    isOpen, 
    onClose, 
    onSubmit, 
    title = "Enter Value", 
    label = "Value:", 
    buttonText = "Submit",
    placeholder = "Enter value...",
    initialValue = "",
    required = false,
    maxLength = null,
    validation = null, // Optional validation function: (value) => string | null (error message)
    inputType = "text",
}: StringInputModalProps) {
    const [inputValue, setInputValue] = useState("");
    const [error, setError] = useState("");

    useEffect(() => {
        if (isOpen) {
            setInputValue(initialValue);
            setError("");
        }
    }, [isOpen, initialValue]);

    useEffect(() => {
        // Handle escape key to close modal
        function handleEscapeKey(event) {
            if (event.key === 'Escape' && isOpen) {
                handleCancel();
            }
        }

        if (isOpen) {
            document.addEventListener('keydown', handleEscapeKey);
            return () => {
                document.removeEventListener('keydown', handleEscapeKey);
            };
        }
    }, [isOpen]);

    function handleCancel() {
        setInputValue("");
        setError("");
        onClose && onClose(false);
    }

    function handleSubmit() {
        const trimmedValue = inputValue.trim();
        
        // Required field validation
        if (required && !trimmedValue) {
            setError("This field is required");
            return;
        }

        // Custom validation
        if (validation && trimmedValue) {
            const validationError = validation(trimmedValue);
            if (validationError) {
                setError(validationError);
                return;
            }
        }

        // Clear error and submit
        setError("");
        onSubmit && onSubmit(trimmedValue);
        setInputValue("");
    }

    function handleInputChange(e: ChangeEvent<HTMLInputElement>) {
        const value = e.target.value;
        
        // Apply max length if specified
        if (maxLength && value.length > maxLength) {
            return;
        }
        
        setInputValue(value);
        
        // Clear error when user starts typing
        if (error) {
            setError("");
        }
    }

    function handleKeyPress(e: KeyboardEvent<HTMLInputElement>) {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
        }
    }

    const footer = (
        <>
            <div className="footerLeft"></div>
            <div className="footerRight">
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCancel}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    className={`btn btn-primary${(!inputValue.trim() && required) || error ? ' disabled' : ''}`}
                    onClick={handleSubmit}
                    disabled={(!inputValue.trim() && required) || !!error}
                >
                    {buttonText}
                </button>
            </div>
        </>
    );

    if (!isOpen) return null;

    return (
        <Modal
            isOpen={isOpen}
            title={title}
            onClose={handleCancel}
            footer={footer}
            className="stringInputModal"
        >
            <div className="stringInputModalBody">
                <div className="form-group">
                    <label htmlFor="stringInput" style={{ 
                        display: 'block', 
                        marginBottom: '8px', 
                        fontWeight: '500' 
                    }}>
                        {label}
                        {required && <span style={{ color: 'red', marginLeft: '4px' }}>*</span>}
                    </label>
                    <input
                        id="stringInput"
                        type={inputType}
                        value={inputValue}
                        onChange={handleInputChange}
                        onKeyPress={handleKeyPress}
                        placeholder={placeholder}
                        className={`form-control${error ? ' error' : ''}`}
                        style={{ 
                            width: '100%', 
                            boxSizing: 'border-box',
                            padding: '8px 12px',
                            fontSize: '14px',
                            borderRadius: '4px',
                            border: error ? '1px solid #dc3545' : '1px solid #ddd'
                        }}
                        autoFocus
                    />
                    {maxLength && (
                        <div style={{ 
                            fontSize: '12px', 
                            color: '#666', 
                            marginTop: '4px',
                            textAlign: 'right'
                        }}>
                            {inputValue.length}/{maxLength}
                        </div>
                    )}
                    {error && (
                        <div style={{ 
                            color: '#dc3545', 
                            fontSize: '12px', 
                            marginTop: '4px' 
                        }}>
                            {error}
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
}

